import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { InfoDialogComponent }  from '@trivelope/triv-ui-tools-lib';
import { Subscription } from 'rxjs';
import { FileBranch, FileDetails } from 'src/app/classes/fileInfo';
import { FileService } from 'src/app/services/file.service';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FolderNameDialog } from './folder-name-dialog.component';
import { AuthenticationService } from 'src/app/services/authentication.service';


@Component({
	selector: 'app-document-tree',
	templateUrl: './document-tree.component.html',
	styleUrls: [ './document-tree.component.css' ]
})
export class DocumentTreeComponent implements OnInit, OnDestroy {


	subscriptions = new Subscription();
	bInProgress = true;
	tree = new FileBranch();
	linearFolders: FileDetails[] = [];

	readonly filesizeMaxMB = 50;
	bUploading = false;

	@ViewChild( "fileInput" ) fileInput!: ElementRef<HTMLInputElement>;
	folderPath = "";

	bAdmin = this.authenticationService.isAdmin();


	constructor(
		private fileService: FileService,
		private matDialog: MatDialog,
		private matSnackBar: MatSnackBar,
		private authenticationService: AuthenticationService
	) {}


	ngOnInit() {

		this.subscriptions.add(
			this.fileService.requestNewFolder$.asObservable().subscribe( path => {
				if( this.bUploading || this.bInProgress ) return;
				this.createFolder( path + "\\" );
			})
		);

		this.subscriptions.add(
			this.fileService.requestFileUpload$.asObservable().subscribe( path => {
				console.warn( "GOT file upload REQUEST", path, this.bUploading, this.bInProgress )
				if( this.bUploading || this.bInProgress ) return;
				this.folderPath = path + "\\";
				this.fileInput.nativeElement.click();
			})
		);

		this.loadTree();

	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	private loadTree() {

		this.bInProgress = true;
		this.fileService.getFiles().subscribe( linearTree => {

			this.bInProgress = false;

			const previousFolders = this.linearFolders;

			const foldersLeft = linearTree.filter( f => f.folder );
			this.linearFolders = [ ...foldersLeft ];

			for( const previousFolder of previousFolders ) {
				const folder = this.linearFolders.find( f => f.hash == previousFolder.hash );
				if( folder ) {
					folder.bCollapsed = previousFolder.bCollapsed;
				}
			}

			const iSubFolders: number[] = [];

			for( let i=0; i < foldersLeft.length; i++ ) {

				for( let j=0; j < foldersLeft.length; j++ ) {
					if( j == i ) continue;

					const path = foldersLeft[i].fileRelativePfad;
					if( path && path.startsWith( foldersLeft[j].fileRelativePfad ) ) {
						iSubFolders.push( i );
						break;
					}
				}
			}

			const hashesToRemove: string[] = [];
			this.tree = new FileBranch();
			this.tree.folder.bCollapsed = false;

			for( let i=0; i < foldersLeft.length; i++ ) {
				if( !iSubFolders.includes(i) ) {
					const branch = new FileBranch();
					branch.folder = foldersLeft[i];
					branch.folder.bCollapsed = false;
					this.tree.subBranches.push( branch );
					hashesToRemove.push( branch.folder.hash );
				}
			}

			for( let i=0; i < foldersLeft.length; i++ ) {
				const folder = foldersLeft[i];
				if( hashesToRemove.includes( folder.hash ) ) {
					foldersLeft.splice( i, 1 );
					i--;
				}
			}

			const onlyFiles = linearTree.filter( f => !f.folder );

			for( const branch of this.tree.subBranches ) {
				this.lookForChildren( branch, foldersLeft, onlyFiles );
			}

			console.log( "TREE", this.tree );
		});

	}

	private lookForChildren( branch: FileBranch, foldersLeft: FileDetails[], onlyFiles: FileDetails[] ) {

		// removal of the last \ if it's the last character of the path
		let folderPath = branch.folder.fileRelativePfad;
		if( !folderPath || !folderPath.length ) {
			console.warn( "invalid folder path", branch.folder );
			return;
		}
		if( folderPath[ folderPath.length - 1 ] == "\\" ) {
			folderPath = folderPath.substring( 0, folderPath.length-1 );
		}


		for( let n=0; n < onlyFiles.length; n++ ) {

			const file = onlyFiles[n];
			if( !file.fileRelativePfad ) {
				console.warn( "no fileRelativePfad", file )
				continue;
			}
			const split = file.fileRelativePfad.split( "\\" );
			if( split.length < 2 ) {
				console.warn( "!split", file.fileRelativePfad )
				continue;	// it should never happen
			}
			split.splice( split.length-1 );
			const path = split.join( "\\" );
			if( path == folderPath ) {

				branch.files.push( file );
				onlyFiles.splice( n, 1 );
				n--;

			}
		}


		for( let n=0 ; n < foldersLeft.length ; n++ ) {

			const folder = foldersLeft[n];
			const split = folder.fileRelativePfad.split( "\\" );
			if( split.length < 2 ) continue;	// it should never happen
			split.splice( split.length-1 );
			const path = split.join( "\\" );
			if( path == folderPath ) {

				const newBranch = new FileBranch();
				newBranch.folder = folder;
				branch.subBranches.push( newBranch );
				foldersLeft.splice( n, 1 );
				n--;

				this.lookForChildren( newBranch, foldersLeft, onlyFiles );
			}

		}

	}


	onFileInputAdd( e: EventTarget | null ) {
		if( e && (<HTMLInputElement>e).files )
			this.onFileAdd( (<HTMLInputElement>e).files || new FileList() );
	}

	onFileAdd( fl: FileList ) {

		if( this.bUploading ) return;
		if( !fl || !fl.length ) return;

		const files: File[] = []
		for( let i = 0; i < fl.length; i++ ) {
			const b = this.checkFile( fl[i] );
			if( !b ) return;
			files.push( fl[i] );
		}

		this.bUploading = true;
		this.bInProgress = true;

		this.fileService.uploadFiles( this.folderPath, files ).subscribe( result => {

			this.bUploading = false;
			this.bInProgress = false;
			this.fileInput.nativeElement.value = "";

			if( result == 413 ) {
				this.matDialog.open( InfoDialogComponent, {
					data: 'Die angehängten Dateien sind zu groß'
				});
			}
			else if( result ) {
				this.matSnackBar.open( 'Erfolgreiches Hochladen', 'Hochladen' );
				this.loadTree();
			}
			else {
				this.matDialog.open( InfoDialogComponent, {
					data: 'Beim Senden der Datei ist ein Fehler aufgetreten'
				});
			}

		});

	}

	private checkFile( file: File ): boolean {

		if( file.size >= this.filesizeMaxMB * 1024 * 1024 ) {
			console.log( 'size too big' );
			this.matDialog.open( InfoDialogComponent, {
				width: '350px',
				data: 'Die maximale Dateigröße beträgt ' + this.filesizeMaxMB + ' MB'
			});
			return false;
		}
		return true;

	}

	createFolder( basePath="" ) {

		this.matDialog.open( FolderNameDialog ).afterClosed().subscribe( path => {

			if( !path ) return;

			this.bUploading = true;
			this.bInProgress = true;

			this.fileService.uploadFiles( basePath + path ).subscribe( result => {

				this.bUploading = false;
				this.bInProgress = false;

				if( result ) {
					this.matSnackBar.open( 'Erfolgreiche Erstellung', 'Hochladen' );
					this.loadTree();
				}
				else {
					this.matDialog.open( InfoDialogComponent, {
						data: 'Beim Senden der Datei ist ein Fehler aufgetreten'
					});
				}

			});

		});

	}

}
